import React, { useRef, useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

export function generateRandomColor() {
  const r = () => Math.floor(100 + Math.random() * 155);
  return `rgb(${r()}, ${r()}, ${r()})`;
}

export default function TrendLineChart({ plotData }) {
  const colorCache = useRef({});
  const [axesSwapped, setAxesSwapped] = useState(false);
  const [yMin, setYMin] = useState("");
  const [yMax, setYMax] = useState("");

  const datasets = useMemo(() => {
    const result = [];
    plotData.forEach((point, idx) => {
      const key = `${point.lat},${point.lon}`;
      if (!colorCache.current[key]) {
        colorCache.current[key] = generateRandomColor();
      }
      const color = colorCache.current[key];

      result.push({
        label: `P${idx + 1}`,
        data: point.time.map((t, i) => axesSwapped
          ? { x: point.displacement[i], y: new Date(t).getTime() }
          : { x: new Date(t).getTime(), y: point.displacement[i] }
        ),
        borderColor: color,
        backgroundColor: color,
        showLine: false,
        pointRadius: 2,
        hoverRadius: 5,
        fill: false,
      });

      if (point.showTrendLine && point.trendPoints?.length) {
        const isPolynomial = point.trendType === "polynomial";
        result.push({
          label: isPolynomial ? `Polynomial Fit P${idx + 1}` : `Linear Trend P${idx + 1}`,
          data: point.trendPoints.map((y, i) => axesSwapped
            ? { x: y, y: new Date(point.time[i]).getTime() }
            : { x: new Date(point.time[i]).getTime(), y }
          ),
          borderColor: color,
          backgroundColor: color,
          borderDash: isPolynomial ? [6, 4] : [2, 4],
          pointRadius: 0,
          fill: false,
          showLine: true,
        });
      }
    });
    return result;
  }, [plotData, axesSwapped]);

  if (!plotData.length) return <div>No data to plot.</div>;

  const parsedMin = yMin !== "" ? Number(yMin) : undefined;
  const parsedMax = yMax !== "" ? Number(yMax) : undefined;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      tooltip: { mode: "index", intersect: false },
    },
    scales: axesSwapped ? {
      x: {
        type: "linear",
        title: { display: true, text: "Displacement (mm)", font: { weight: "bold" } },
        min: parsedMin,
        max: parsedMax,
      },
      y: {
        type: "time",
        time: {
          unit: "month",
          tooltipFormat: "yyyy-MM-dd",
          displayFormats: { month: "MMM yyyy" },
        },
        title: { display: true, text: "Date", font: { weight: "bold" } },
        ticks: { maxTicksLimit: 6 },
      },
    } : {
      x: {
        type: "time",
        time: {
          unit: "month",
          tooltipFormat: "yyyy-MM-dd",
          displayFormats: { month: "MMM yyyy" },
        },
        title: { display: true, text: "Date", font: { weight: "bold" } },
        ticks: { maxTicksLimit: 6 },
      },
      y: {
        title: { display: true, text: "Displacement (mm)", font: { weight: "bold" } },
        beginAtZero: parsedMin === undefined && parsedMax === undefined,
        min: parsedMin,
        max: parsedMax,
      },
    },
  };

  const inputStyle = {
    width: 64, padding: "2px 5px", fontSize: 11,
    border: "1px solid #d1d5db", borderRadius: 4,
    color: "#374151", outline: "none",
  };

  return (
    <div>
      {/* Axis controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b7280", cursor: "pointer", userSelect: "none" }}>
          <input type="checkbox" checked={axesSwapped} onChange={e => setAxesSwapped(e.target.checked)}
            style={{ accentColor: "#232231", cursor: "pointer" }} />
          Swap Axes
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b7280" }}>
          {axesSwapped ? "X" : "Y"} min
          <input type="number" value={yMin} onChange={e => setYMin(e.target.value)} placeholder="auto" style={inputStyle} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b7280" }}>
          {axesSwapped ? "X" : "Y"} max
          <input type="number" value={yMax} onChange={e => setYMax(e.target.value)} placeholder="auto" style={inputStyle} />
        </label>
        {(yMin !== "" || yMax !== "") && (
          <button onClick={() => { setYMin(""); setYMax(""); }}
            style={{ fontSize: 10, color: "#6b7280", background: "none", border: "1px solid #d1d5db",
              borderRadius: 4, padding: "2px 7px", cursor: "pointer" }}>
            Reset
          </button>
        )}
      </div>
      <div style={{ width: "100%", height: "300px" }}>
        <Line data={{ datasets }} options={options} />
      </div>
    </div>
  );
}
